import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, RefreshCw, Package, Loader2 } from "lucide-react";

export default function PrintifyAdmin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [mappings, setMappings] = useState({});
  const [blueprints, setBlueprints] = useState([]);
  const [bpSearch, setBpSearch] = useState("");
  const [expanded, setExpanded] = useState(null); // template_id currently being mapped
  const [providers, setProviders] = useState({}); // by blueprint_id
  const [variants, setVariants] = useState({});   // by "bp:pv"
  const [busy, setBusy] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);      // { created, failed, unsynced, ... }
  const [retryJob, setRetryJob] = useState(null);          // { job_id, total, done, created, failed, status }
  const [retryPolling, setRetryPolling] = useState(false);

  useEffect(() => { if (!loading && (!user || !user.is_admin)) nav("/auth"); }, [user, loading, nav]);

  const load = useCallback(async () => {
    try {
      const [s, t, m, ss, rj] = await Promise.all([
        api.get("/admin/printify/status"),
        api.get("/admin/product-templates"),
        api.get("/admin/printify/mappings"),
        api.get("/admin/printify/sync-status").catch(() => ({data: null})),
        api.get("/admin/printify/retry-status").catch(() => ({data: null})),
      ]);
      setStatus(s.data);
      setTemplates(t.data.templates.filter(x => x.fulfillment_source === "PRINTIFY"));
      const map = {};
      (m.data.mappings || []).forEach(x => { map[x.template_id] = x; });
      setMappings(map);
      setSyncStatus(ss.data?.summary || null);
      if (rj.data && rj.data.job_id) setRetryJob(rj.data);
    } catch { toast.error("Failed to load Printify status"); }
  }, []);

  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);

  const searchBlueprints = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/printify/blueprints", { params: { q: bpSearch } });
      setBlueprints(r.data.blueprints);
    } catch { toast.error("Blueprint search failed"); }
    finally { setBusy(false); }
  };

  const loadProviders = async (bpId) => {
    if (providers[bpId]) return;
    try {
      const r = await api.get(`/admin/printify/blueprints/${bpId}/providers`);
      setProviders(p => ({...p, [bpId]: r.data.providers}));
    } catch { toast.error("Providers fetch failed"); }
  };

  const loadVariants = async (bpId, pvId) => {
    const key = `${bpId}:${pvId}`;
    if (variants[key]) return;
    try {
      const r = await api.get(`/admin/printify/blueprints/${bpId}/providers/${pvId}/variants`);
      setVariants(v => ({...v, [key]: r.data.variants}));
    } catch { toast.error("Variants fetch failed"); }
  };

  const saveMapping = async (template_id, blueprint_id, provider_id, variant_ids) => {
    try {
      await api.put("/admin/printify/mappings", { template_id, blueprint_id, provider_id, variant_ids });
      toast.success("Mapping saved");
      setExpanded(null);
      load();
    } catch { toast.error("Save failed"); }
  };

  // ---- Retry-all-failed + progress polling ----
  const startRetryAll = async () => {
    try {
      const r = await api.post("/admin/printify/retry-all-failed");
      if (!r.data.queued) { toast.success("Nothing to retry — everything is already synced"); return; }
      toast.info(`Queued ${r.data.queued} products for retry`);
      setRetryJob({ job_id: r.data.job_id, total: r.data.queued, done: 0, created: 0, failed: 0, status: "running" });
      setRetryPolling(true);
    } catch { toast.error("Failed to start retry"); }
  };

  useEffect(() => {
    if (!retryJob || retryJob.status === "complete") { setRetryPolling(false); return; }
    let cancel = false;
    const tick = async () => {
      try {
        const r = await api.get(`/admin/printify/retry-status/${retryJob.job_id}`);
        if (cancel) return;
        setRetryJob(r.data);
        if (r.data.status === "complete") {
          setRetryPolling(false);
          toast.success(`Sync complete — ${r.data.created} succeeded${r.data.failed ? `, ${r.data.failed} failed` : ""}`);
          // Refresh sync counts
          const ss = await api.get("/admin/printify/sync-status");
          setSyncStatus(ss.data?.summary || null);
        }
      } catch { /* keep polling */ }
    };
    const id = setInterval(tick, 2000);
    return () => { cancel = true; clearInterval(id); };
  }, [retryJob?.job_id, retryJob?.status]);

  if (!user?.is_admin) return null;

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="printify-page">
      <h1 className="font-display text-5xl text-[color:var(--hb-navy)]">Printify Integration</h1>
      <p className="text-sm text-neutral-600 mt-2">Connect your Printify shop and map each in-house template to a Printify blueprint (product type).</p>

      {/* ---- CONNECTION STATUS ---- */}
      <div className="mt-8 border border-neutral-200 bg-white p-6" data-testid="printify-status-card">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl">Connection</h2>
          <button onClick={load} className="text-xs uppercase tracking-widest border border-neutral-300 px-3 py-1 flex items-center gap-1">
            <RefreshCw size={12}/> Refresh
          </button>
        </div>
        {!status ? (
          <div className="text-sm text-neutral-500 mt-4">Loading…</div>
        ) : !status.configured ? (
          <div className="mt-4 flex items-center gap-3 text-amber-700 bg-amber-50 border border-amber-200 p-4">
            <AlertCircle size={20}/>
            <div className="text-sm">
              <div className="font-medium">Not configured</div>
              <div className="text-xs mt-1">{status.error}</div>
              <div className="text-xs mt-2">Set <code className="bg-white px-1">PRINTIFY_API_TOKEN</code> and <code className="bg-white px-1">PRINTIFY_SHOP_ID</code> in backend/.env, then restart backend.</div>
            </div>
          </div>
        ) : status.error ? (
          <div className="mt-4 flex items-center gap-3 text-red-700 bg-red-50 border border-red-200 p-4">
            <AlertCircle size={20}/>
            <div className="text-sm">
              <div className="font-medium">Connected but API error</div>
              <div className="text-xs mt-1 font-mono break-all">{status.error}</div>
            </div>
          </div>
        ) : (
          <div className="mt-4 grid sm:grid-cols-3 gap-4">
            <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200">
              <CheckCircle2 size={20} className="text-green-700 shrink-0 mt-0.5"/>
              <div className="text-sm">
                <div className="font-medium">Connected</div>
                <div className="text-xs text-neutral-600 mt-1">{status.shop?.title}</div>
                <div className="text-[10px] text-neutral-500 mt-1">Shop ID: {status.shop_id}</div>
                <div className="text-[10px] text-neutral-500">Channel: {status.shop?.sales_channel}</div>
              </div>
            </div>
            <div className="p-4 border border-neutral-200">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Webhooks</div>
              <div className="text-2xl font-medium">{status.webhooks_registered}</div>
              <div className="text-[10px] text-neutral-500">registered</div>
            </div>
            <div className="p-4 border border-neutral-200">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Mapped Templates</div>
              <div className="text-2xl font-medium">{Object.keys(mappings).length} / {templates.length}</div>
              <div className="text-[10px] text-neutral-500">PRINTIFY templates configured</div>
            </div>
          </div>
        )}
      </div>

      {/* ---- PRODUCT SYNC STATUS + RETRY ---- */}
      <div className="mt-6 border border-neutral-200 bg-white p-6" data-testid="sync-status-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-display text-2xl">Product Sync Status</h2>
          <button
            onClick={startRetryAll}
            disabled={retryPolling}
            className="text-xs uppercase tracking-widest border border-[color:var(--hb-navy)] bg-[color:var(--hb-navy)] text-white px-4 py-2 flex items-center gap-2 disabled:opacity-50"
            data-testid="retry-all-btn"
          >
            {retryPolling ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>}
            {retryPolling ? "Syncing…" : "Retry All Failed"}
          </button>
        </div>
        {syncStatus && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="sync-status-grid">
            <div className="p-3 bg-green-50 border border-green-200">
              <div className="text-[10px] uppercase tracking-widest text-green-800">Created</div>
              <div className="text-2xl font-medium text-green-900" data-testid="status-created">{syncStatus.created || 0}</div>
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200">
              <div className="text-[10px] uppercase tracking-widest text-amber-800">Unsynced</div>
              <div className="text-2xl font-medium text-amber-900" data-testid="status-unsynced">{syncStatus.unsynced || 0}</div>
            </div>
            <div className="p-3 bg-red-50 border border-red-200">
              <div className="text-[10px] uppercase tracking-widest text-red-800">Failed</div>
              <div className="text-2xl font-medium text-red-900" data-testid="status-failed">{syncStatus.failed || 0}</div>
            </div>
            <div className="p-3 bg-neutral-50 border border-neutral-200">
              <div className="text-[10px] uppercase tracking-widest text-neutral-600">Pending</div>
              <div className="text-2xl font-medium text-neutral-900" data-testid="status-pending">{syncStatus.pending || 0}</div>
            </div>
          </div>
        )}
        {retryJob && (
          <div className="mt-4" data-testid="retry-progress">
            <div className="flex justify-between text-xs text-neutral-600 mb-1.5">
              <span>Job {retryJob.job_id}</span>
              <span data-testid="retry-counter">
                {retryJob.done} / {retryJob.total}
                {retryJob.failed > 0 && <span className="ml-2 text-red-600">({retryJob.failed} failed)</span>}
              </span>
            </div>
            <div className="h-3 bg-neutral-100 border border-neutral-200 overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${retryJob.status === "complete" ? "bg-green-500" : "bg-[color:var(--hb-royal)]"}`}
                style={{ width: `${retryJob.total > 0 ? Math.round((retryJob.done / retryJob.total) * 100) : 0}%` }}
                data-testid="retry-progress-bar"
              />
            </div>
            {retryJob.status === "complete" && (
              <div className="mt-2 text-xs text-green-700 flex items-center gap-1.5"><CheckCircle2 size={12}/> Sync complete</div>
            )}
          </div>
        )}
      </div>

      {/* ---- BLUEPRINT SEARCH ---- */}
      <div className="mt-8 border border-neutral-200 bg-white p-6" data-testid="blueprint-search">
        <h2 className="font-display text-2xl">Search Printify Blueprints</h2>
        <p className="text-xs text-neutral-500 mt-1">Find a Printify product type (e.g., "hoodie", "unisex tee", "tote") to link to your templates.</p>
        <div className="mt-4 flex gap-2">
          <input value={bpSearch} onChange={e=>setBpSearch(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&searchBlueprints()}
            placeholder="Search blueprints…"
            className="flex-1 border border-neutral-300 px-3 py-2 text-sm" data-testid="bp-search-input"/>
          <button onClick={searchBlueprints} disabled={busy} className="hb-btn-primary" data-testid="bp-search-btn">
            {busy ? "Searching…" : "Search"}
          </button>
        </div>
        {blueprints.length > 0 && (
          <div className="mt-4 max-h-72 overflow-y-auto divide-y divide-neutral-100">
            {blueprints.map(b => (
              <div key={b.id} className="py-2 flex items-center gap-3 text-sm">
                {b.images?.[0] && <img src={b.images[0]} alt="" className="w-10 h-10 object-cover"/>}
                <div className="flex-1">
                  <div className="font-medium">{b.title}</div>
                  <div className="text-[10px] text-neutral-500">Blueprint ID: {b.id} · Brand: {b.brand}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ---- TEMPLATE MAPPINGS ---- */}
      <div className="mt-8 border border-neutral-200 bg-white p-6" data-testid="mappings-card">
        <h2 className="font-display text-2xl">Template → Printify Blueprint Mapping</h2>
        <p className="text-xs text-neutral-500 mt-1">Link each PRINTIFY template to a specific Printify blueprint + print provider. Products created for these templates will be pushed to Printify automatically.</p>
        <div className="mt-4 space-y-3">
          {templates.map(t => {
            const m = mappings[t.template_id];
            const isOpen = expanded === t.template_id;
            return (
              <div key={t.template_id} className="border border-neutral-200" data-testid={`mapping-${t.template_id}`}>
                <div className="flex items-center gap-4 p-3">
                  <Package size={16} className="text-[color:var(--hb-royal)]"/>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{t.name}</div>
                    <div className="text-[10px] text-neutral-500">
                      {m ? (
                        <>Mapped to blueprint <b>{m.blueprint_id}</b>, provider <b>{m.provider_id}</b>{m.variant_ids?.length ? `, ${m.variant_ids.length} variants` : ""}</>
                      ) : (
                        <span className="text-amber-600">Not mapped yet</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={()=>{ setExpanded(isOpen?null:t.template_id); if (!isOpen) searchBlueprintsFor(t.name); }}
                    className="text-xs uppercase tracking-widest border border-neutral-300 px-3 py-1"
                    data-testid={`configure-${t.template_id}`}
                  >
                    {m ? "Change" : "Configure"}
                  </button>
                </div>
                {isOpen && (
                  <MappingEditor
                    templateId={t.template_id}
                    templateName={t.name}
                    initial={m}
                    onSave={saveMapping}
                    onCancel={()=>setExpanded(null)}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Helper: prefill search input based on template name
  function searchBlueprintsFor(name) {
    const t = name.toLowerCase().replace(/sweatshirt|shirt|top|bag/g, "").trim();
    setBpSearch(t);
  }
}

function MappingEditor({ templateId, templateName, initial, onSave, onCancel }) {
  const [bpId, setBpId] = useState(initial?.blueprint_id || "");
  const [pvId, setPvId] = useState(initial?.provider_id || "");
  const [variantIds, setVariantIds] = useState((initial?.variant_ids || []).join(","));
  const [providers, setProviders] = useState([]);
  const [variants, setVariants] = useState([]);
  const [loadingP, setLoadingP] = useState(false);
  const [loadingV, setLoadingV] = useState(false);

  const fetchProviders = async (id) => {
    setLoadingP(true);
    try {
      const r = await api.get(`/admin/printify/blueprints/${id}/providers`);
      setProviders(r.data.providers);
    } catch { toast.error("Provider fetch failed"); }
    finally { setLoadingP(false); }
  };

  const fetchVariants = async (bp, pv) => {
    setLoadingV(true);
    try {
      const r = await api.get(`/admin/printify/blueprints/${bp}/providers/${pv}/variants`);
      const list = r.data.variants?.variants || r.data.variants || [];
      setVariants(list);
    } catch { toast.error("Variants fetch failed"); }
    finally { setLoadingV(false); }
  };

  return (
    <div className="border-t border-neutral-200 bg-neutral-50 p-4 space-y-3">
      <div>
        <label className="text-[10px] uppercase tracking-widest text-neutral-500">Blueprint ID</label>
        <div className="flex gap-2 mt-1">
          <input value={bpId} onChange={e=>setBpId(e.target.value.replace(/\D/g,""))}
            placeholder="e.g., 5 (Bella+Canvas 3001)" className="flex-1 border border-neutral-300 px-2 py-1 text-sm"
            data-testid={`bp-input-${templateId}`}/>
          <button onClick={()=>bpId && fetchProviders(bpId)} disabled={loadingP}
            className="text-xs uppercase tracking-widest border border-neutral-300 px-3 py-1">
            {loadingP ? "…" : "Load Providers"}
          </button>
        </div>
      </div>
      {providers.length > 0 && (
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Print Provider</label>
          <select value={pvId} onChange={e=>{ setPvId(e.target.value); if(e.target.value) fetchVariants(bpId, e.target.value); }}
            className="mt-1 w-full border border-neutral-300 px-2 py-1 text-sm bg-white" data-testid={`provider-select-${templateId}`}>
            <option value="">— Select provider —</option>
            {providers.map(p => (<option key={p.id} value={p.id}>{p.title} (ID {p.id})</option>))}
          </select>
        </div>
      )}
      {loadingV && <div className="text-xs text-neutral-500"><Loader2 size={12} className="inline animate-spin mr-1"/>Loading variants…</div>}
      {variants.length > 0 && (
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Variant IDs (comma-separated)</label>
          <input value={variantIds} onChange={e=>setVariantIds(e.target.value)}
            placeholder="e.g., 12100,12101,12102"
            className="mt-1 w-full border border-neutral-300 px-2 py-1 text-sm font-mono"
            data-testid={`variants-input-${templateId}`}/>
          <div className="mt-2 max-h-40 overflow-y-auto border border-neutral-200 bg-white p-2 text-xs">
            {variants.slice(0, 100).map(v => (
              <div key={v.id} className="flex items-center gap-2 py-0.5 border-b border-neutral-100 last:border-0">
                <button type="button" onClick={()=>{
                  const set = new Set(variantIds.split(",").map(x=>x.trim()).filter(Boolean));
                  set.has(String(v.id)) ? set.delete(String(v.id)) : set.add(String(v.id));
                  setVariantIds([...set].join(","));
                }} className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 ${variantIds.split(",").map(x=>x.trim()).includes(String(v.id))?"bg-[color:var(--hb-navy)] text-white border-[color:var(--hb-navy)]":"border-neutral-300"}`}>
                  {variantIds.split(",").map(x=>x.trim()).includes(String(v.id)) ? "✓" : "+"}
                </button>
                <span className="font-mono text-neutral-500">{v.id}</span>
                <span className="flex-1">{v.title || (v.options && Object.values(v.options).join(" / "))}</span>
                {v.placeholders && <span className="text-neutral-400 text-[10px]">{v.placeholders.map(p=>p.position).join(",")}</span>}
              </div>
            ))}
            {variants.length > 100 && <div className="text-[10px] text-neutral-500 pt-1">… and {variants.length-100} more</div>}
          </div>
        </div>
      )}
      <div className="flex gap-2 pt-2">
        <button
          onClick={()=>{
            const vIds = variantIds.split(",").map(x=>parseInt(x.trim())).filter(Number.isFinite);
            if (!bpId || !pvId) { toast.error("Blueprint and Provider required"); return; }
            onSave(templateId, parseInt(bpId), parseInt(pvId), vIds);
          }}
          className="hb-btn-primary" data-testid={`save-mapping-${templateId}`}>Save Mapping</button>
        <button onClick={onCancel} className="text-xs uppercase tracking-widest border border-neutral-300 px-3 py-2">Cancel</button>
      </div>
    </div>
  );
}
